import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Trophy, Lightning, TrendUp, CurrencyDollar, Users, FileText, Globe,
  Translate, ArrowUp, CircleNotch, CheckCircle
} from '@phosphor-icons/react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('nx_token')}` } });

export default function CreatorWorkspacePage() {
  const [dash, setDash] = useState(null);
  const [card, setCard] = useState(null);
  const [commerce, setCommerce] = useState(null);
  const [vern, setVern] = useState(null);
  const [citations, setCitations] = useState(null);
  const [genPitch, setGenPitch] = useState(false);
  const [liType, setLiType] = useState('brand_partnership');
  const [liAmount, setLiAmount] = useState('');
  const [liBrand, setLiBrand] = useState('');
  const [roiProduct, setRoiProduct] = useState('');
  const [roiViews, setRoiViews] = useState('10000');
  const [roiComm, setRoiComm] = useState('6');
  const [roiResult, setRoiResult] = useState(null);
  const [calcingROI, setCalcingROI] = useState(false);
  const [forecast, setForecast] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/creator/dashboard`, auth()),
      axios.get(`${API}/creator/marketplace-card`, auth()),
      axios.get(`${API}/creator/commerce-hub`, auth()),
      axios.get(`${API}/creator/vernacular-optimizer`, auth()),
      axios.get(`${API}/creator/citation-intel`, auth()),
    ]).then(([d, c, cm, v, ci]) => { setDash(d.data); setCard(c.data); setCommerce(cm.data); setVern(v.data); setCitations(ci.data); });
    axios.get(`${API}/creator/income-forecast`, auth()).then(r => setForecast(r.data)).catch(() => {});
  }, []);

  const handlePitch = async () => {
    setGenPitch(true);
    try { await axios.post(`${API}/creator/pitch-deck`, {}, auth()); toast.success('Pitch deck generated'); }
    catch { toast.error('Failed'); } finally { setGenPitch(false); }
  };

  const handleAddLinkedIn = async () => {
    if (!liAmount) return;
    try {
      await axios.post(`${API}/creator/linkedin-revenue?amount=${liAmount}&entry_type=${liType}&brand_name=${encodeURIComponent(liBrand)}`, {}, auth());
      toast.success('Entry added');
      setLiAmount(''); setLiBrand('');
      const cm = await axios.get(`${API}/creator/commerce-hub`, auth());
      setCommerce(cm.data);
    } catch { toast.error('Failed'); }
  };

  const handleCalcROI = async () => {
    setCalcingROI(true);
    try {
      const r = await axios.post(`${API}/creator/reel-roi?product=${encodeURIComponent(roiProduct)}&views=${roiViews}&commission=${roiComm}`, {}, auth());
      setRoiResult(r.data);
    } catch { toast.error('Calculation failed'); }
    finally { setCalcingROI(false); }
  };

  const trendData = dash?.som_trend?.map((v, i) => ({ day: i + 1, som: v })) || [];

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="creator-workspace">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-black text-[var(--text-primary)] tracking-tight">Creator Command Center</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">Your AI discoverability, revenue, and brand authority — in one view</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="pulse-dot-green" />
            <span className="text-[9px] font-mono text-[var(--success)] uppercase tracking-wider">Creator Mode Active</span>
          </div>
        </div>

        {/* 4 Core KPIs */}
        {dash && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 creator-kpi-grid">
            <div className="nx-metric-card rounded-md min-h-[120px]">
              <div className="flex items-center gap-1.5 mb-2"><Trophy size={12} className="text-[var(--accent)]" weight="fill" /><span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">Personal SoM</span></div>
              <p className="text-3xl font-black text-[var(--accent)] font-mono">{dash.personal_som}%</p>
              <p className="text-[9px] text-[var(--text-tertiary)] font-mono mt-1">Rank #{dash.som_rank} of {dash.som_total_tracked}</p>
              {/* AF-3.20.2 — the niche-average comparison here was previously
                  computed client-side from Math.random() (personal_som * 0.8
                  + a random 0-8 offset), a fabricated peer benchmark with no
                  backend source at all -- removed. There is no real
                  niche-average field in this API response, so this state is
                  honestly NOT_AVAILABLE rather than invented. */}
              <p className="text-[8px] text-[var(--text-tertiary)] mt-1.5 italic">Niche benchmark not available — {dash.som_total_tracked} creator{dash.som_total_tracked === 1 ? '' : 's'} tracked in your niche</p>
            </div>
            <div className="nx-metric-card rounded-md">
              <div className="flex items-center gap-1.5 mb-2"><Lightning size={12} className="text-[var(--info)]" weight="fill" /><span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">AI Citations</span></div>
              <p className="text-3xl font-black text-[var(--info)] font-mono">{dash.ai_citations_month}</p>
              <p className="text-[9px] text-[var(--text-tertiary)] font-mono mt-1">This month</p>
            </div>
            <div className="nx-metric-card rounded-md">
              <div className="flex items-center gap-1.5 mb-2"><CurrencyDollar size={12} className="text-[var(--success)]" weight="fill" /><span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">Affiliate Revenue</span></div>
              <p className="text-3xl font-black text-[var(--success)] font-mono">INR {dash.affiliate_revenue_month?.toLocaleString()}</p>
            </div>
            <div className="nx-metric-card rounded-md">
              <div className="flex items-center gap-1.5 mb-2"><Users size={12} className="text-[var(--warning)]" weight="fill" /><span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">Brand Enquiries</span></div>
              <p className="text-3xl font-black text-[var(--warning)] font-mono">{dash.inbound_enquiries}</p>
            </div>
          </div>
        )}

        {/* Citation Source Breakdown */}
        {dash?.citation_breakdown && (
          <div className="nx-metric-card rounded-md">
            <p className="text-[10px] text-[var(--text-secondary)] mb-2">Your content appears in <b className="text-[var(--text-primary)]">{dash.ai_citations_month}</b> AI responses for your niche this month.</p>
            <div className="flex items-center gap-4 mb-2">
              {[
                { platform: 'LinkedIn', count: dash.citation_breakdown.linkedin, color: 'var(--accent)' },
                { platform: 'YouTube', count: dash.citation_breakdown.youtube, color: 'var(--error)' },
                { platform: 'Instagram', count: dash.citation_breakdown.instagram, color: 'var(--warning)' },
                { platform: 'Other', count: dash.citation_breakdown.other, color: 'var(--text-tertiary)' },
              ].map(p => (
                <div key={p.platform} className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
                  <span className="text-[9px] text-[var(--text-secondary)]">{p.platform}:</span>
                  <span className="text-[10px] font-mono text-[var(--text-primary)] font-bold">{p.count}</span>
                </div>
              ))}
            </div>
            {dash.linkedin_insight && <p className="text-[8px] text-[var(--info)] leading-relaxed italic">{dash.linkedin_insight}</p>}
          </div>
        )}

        {/* SoM Trend */}
        {trendData.length > 0 && (
          <div className="nx-metric-card rounded-md">
            <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">Personal SoM — 30-Day Trend</h3>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="day" tick={{ fill: 'var(--text-tertiary)', fontSize: 9 }} axisLine={false} />
                  <YAxis tick={{ fill: 'var(--text-tertiary)', fontSize: 9 }} axisLine={false} />
                  <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 11 }} />
                  <Line type="monotone" dataKey="som" stroke="var(--accent)" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <Tabs defaultValue="commerce" className="space-y-4">
          <TabsList className="bg-[var(--surface)] border border-[var(--border)]">
            <TabsTrigger value="commerce" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white text-xs">Commerce Hub</TabsTrigger>
            <TabsTrigger value="citations" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white text-xs">Citation Intel</TabsTrigger>
            <TabsTrigger value="vernacular" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white text-xs">Language Optimizer</TabsTrigger>
            <TabsTrigger value="card" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white text-xs">Marketplace Card</TabsTrigger>
            <TabsTrigger value="pitch" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white text-xs">Pitch Deck</TabsTrigger>
          </TabsList>

          {/* Commerce Hub */}
          <TabsContent value="commerce">
            {commerce && (
              <div className="space-y-4">
                <div className="nx-metric-card rounded-md">
                  <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">Multi-Platform Revenue</h3>
                  <p className="text-3xl font-black text-[var(--success)] font-mono mb-4">INR {commerce.total_revenue?.toLocaleString()}</p>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {Object.entries(commerce.platforms || {}).map(([p, v]) => (
                      <div key={p} className="p-2 rounded border border-[var(--border)] text-center">
                        <p className="text-xs text-[var(--text-tertiary)] capitalize">{p.replace('_', ' ')}</p>
                        <p className="text-sm font-bold text-[var(--text-primary)] font-mono">INR {v?.toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </div>
                {commerce.commission_benchmark && (
                  <div className="hitl-card">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[9px] font-mono text-[var(--warning)] uppercase tracking-wider">Commission Optimiser</span>
                    </div>
                    {/* Horizontal bar chart */}
                    <div className="space-y-3 mb-3">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[9px] text-[var(--text-secondary)]">Your Rate</span>
                          <span className="text-[10px] font-mono text-[var(--text-primary)]">{commerce.commission_benchmark.your_rate}%</span>
                        </div>
                        <div className="w-full h-5 bg-[var(--border-light)] rounded overflow-hidden">
                          <div className="h-full rounded bg-[var(--accent)]" style={{ width: `${(commerce.commission_benchmark.your_rate / 15) * 100}%` }} />
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[9px] text-[var(--text-secondary)]">Category Average</span>
                          <span className="text-[10px] font-mono text-[var(--text-secondary)]">{commerce.commission_benchmark.category_avg}%</span>
                        </div>
                        <div className="w-full h-5 bg-[var(--border-light)] rounded overflow-hidden">
                          <div className="h-full rounded bg-[var(--text-tertiary)]" style={{ width: `${(commerce.commission_benchmark.category_avg / 15) * 100}%` }} />
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[9px] text-[var(--warning)]">Gap (Lost Revenue)</span>
                          <span className="text-[10px] font-mono text-[var(--warning)]">{(commerce.commission_benchmark.category_avg - commerce.commission_benchmark.your_rate).toFixed(1)}%</span>
                        </div>
                        <div className="w-full h-5 bg-[var(--border-light)] rounded overflow-hidden">
                          <div className="h-full rounded bg-[var(--warning)]" style={{ width: `${((commerce.commission_benchmark.category_avg - commerce.commission_benchmark.your_rate) / 15) * 100}%` }} />
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-[var(--warning)] font-mono mb-2">Estimated monthly gap: INR {commerce.commission_benchmark.monthly_gap?.toLocaleString()}</p>
                    <Button size="sm" className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white text-[10px] h-7">Request Rate Review</Button>
                  </div>
                )}

                {/* ═══ ADD 1: LinkedIn Revenue Section ═══ */}
                {commerce.linkedin_revenue && (
                  <div className="nx-metric-card rounded-md">
                    <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-1">LinkedIn Revenue</h3>
                    <p className="text-[10px] text-[var(--text-secondary)] mb-4">Log your B2B income — brand deals, consulting, speaking, and product sales.</p>
                    <div className="flex items-center gap-3 mb-3">
                      {['brand_partnership', 'consulting', 'speaking', 'product_course'].map(t => (
                        <button key={t} onClick={() => setLiType(t)}
                          className={`px-2.5 py-1 text-[9px] font-mono rounded ${liType === t ? 'bg-[var(--success)] text-white' : 'bg-[var(--neutral-bg)] text-[var(--text-tertiary)]'}`}>
                          {t.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <Input value={liAmount} onChange={e => setLiAmount(e.target.value)} type="number" placeholder="INR" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)] font-mono text-[10px] w-24" />
                      <Input value={liBrand} onChange={e => setLiBrand(e.target.value)} placeholder="Client / Brand" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)] text-[10px] flex-1" />
                      <Button size="sm" onClick={handleAddLinkedIn} className="bg-[var(--success)] text-white text-[9px] h-8 px-3">Add</Button>
                    </div>
                    <p className="text-xs text-[var(--text-primary)] font-mono mb-2">This month: <b className="text-[var(--success)]">INR {(commerce.linkedin_revenue.total_this_month || 0).toLocaleString()}</b></p>
                    {commerce.linkedin_revenue.monthly_trend?.length > 0 && (
                      <div className="h-20 mb-2">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={commerce.linkedin_revenue.monthly_trend}>
                            <XAxis dataKey="month" tick={{ fill: 'var(--text-tertiary)', fontSize: 8 }} axisLine={false} />
                            <Bar dataKey="amount" fill="var(--success)" radius={[3, 3, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                    {commerce.linkedin_revenue.insight && <p className="text-[8px] text-[var(--info)] italic">{commerce.linkedin_revenue.insight}</p>}
                    {commerce.linkedin_revenue.entries?.length > 0 && (
                      <table className="w-full text-[9px] font-mono border-collapse mt-2">
                        <thead><tr className="border-b border-[var(--border)] text-[var(--text-tertiary)]"><th className="text-left p-1">Date</th><th className="p-1">Type</th><th className="p-1">Client</th><th className="text-right p-1">Amount</th></tr></thead>
                        <tbody>{commerce.linkedin_revenue.entries.slice(0, 10).map(e => (
                          <tr key={e.id} className="border-b border-[var(--border-light)]"><td className="p-1 text-[var(--text-secondary)]">{e.date}</td><td className="p-1 capitalize">{e.type?.replace(/_/g, ' ')}</td><td className="p-1 text-[var(--text-primary)]">{e.brand_name}</td><td className="p-1 text-right text-[var(--success)]">INR {e.amount?.toLocaleString()}</td></tr>
                        ))}</tbody>
                      </table>
                    )}
                  </div>
                )}

                {/* ═══ ADD 2: Reel ROI Calculator ═══ */}
                <div className="nx-metric-card rounded-md">
                  <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-1">Reel ROI Calculator</h3>
                  <p className="text-[10px] text-[var(--text-secondary)] mb-3">Estimate income from a planned Reel before you create it.</p>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <Input value={roiProduct} onChange={e => setRoiProduct(e.target.value)} placeholder="Product name" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)] text-[10px]" />
                    <Input value={roiViews} onChange={e => setRoiViews(e.target.value)} type="number" placeholder="Expected views" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)] text-[10px]" />
                    <Input value={roiComm} onChange={e => setRoiComm(e.target.value)} type="number" placeholder="Commission %" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)] text-[10px]" />
                  </div>
                  <Button size="sm" onClick={handleCalcROI} disabled={calcingROI} className="bg-[var(--accent)] text-white text-[9px] h-7 mb-3">
                    {calcingROI ? 'Calculating...' : 'Calculate ROI'}
                  </Button>
                  {roiResult && (
                    <div className="grid grid-cols-3 gap-3 animate-fade-in-up">
                      <div className="p-2.5 rounded border border-[var(--success-border)] bg-[var(--success-bg)] text-center">
                        <p className="text-[8px] text-[var(--text-tertiary)]">Projected Income</p>
                        <p className="text-lg font-black text-[var(--success)] font-mono">INR {roiResult.projected_income?.toLocaleString()}</p>
                      </div>
                      <div className="p-2.5 rounded border border-[var(--accent-border)] bg-[var(--accent-light)] text-center">
                        <p className="text-[8px] text-[var(--text-tertiary)]">Citation Probability</p>
                        <p className="text-lg font-black text-[var(--accent)] font-mono">{roiResult.citation_probability}%</p>
                        <p className="text-[7px] text-[var(--info)] mt-0.5">{roiResult.improvement_tip}</p>
                      </div>
                      <div className="p-2.5 rounded border border-[var(--border)] text-center">
                        <p className="text-[8px] text-[var(--text-tertiary)]">Income Range</p>
                        <p className="text-sm font-bold text-[var(--text-primary)] font-mono">INR {roiResult.low_estimate?.toLocaleString()} — {roiResult.high_estimate?.toLocaleString()}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* ═══ ADD 3: Income Forecast ═══ */}
                <div className="nx-metric-card rounded-md">
                  <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-1">12-Week Income Forecast</h3>
                  <p className="text-[10px] text-[var(--text-secondary)] mb-3">Projected income based on your current content pace and rates.</p>
                  {forecast ? (
                    <>
                      <div className="h-36 mb-3">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={forecast.weeks}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                            <XAxis dataKey="week" tick={{ fill: 'var(--text-tertiary)', fontSize: 8 }} axisLine={false} tickFormatter={v => `W${v}`} />
                            <YAxis tick={{ fill: 'var(--text-tertiary)', fontSize: 8 }} axisLine={false} />
                            <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 10 }} />
                            <Line type="monotone" dataKey="affiliate" stroke="var(--accent)" strokeWidth={2} dot={false} name="Affiliate" />
                            <Line type="monotone" dataKey="linkedin" stroke="var(--success)" strokeWidth={2} dot={false} name="LinkedIn" />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="p-2 rounded border border-[var(--border)] text-center">
                          <p className="text-[8px] text-[var(--text-tertiary)]">4-Week</p>
                          <p className="text-sm font-bold text-[var(--text-primary)] font-mono">INR {forecast.total_4w?.toLocaleString()}</p>
                        </div>
                        <div className="p-2 rounded border border-[var(--border)] text-center">
                          <p className="text-[8px] text-[var(--text-tertiary)]">12-Week</p>
                          <p className="text-sm font-bold text-[var(--accent)] font-mono">INR {forecast.total_12w?.toLocaleString()}</p>
                        </div>
                        <div className="p-2 rounded border border-[var(--success-border)] bg-[var(--success-bg)] text-center">
                          <p className="text-[8px] text-[var(--text-tertiary)]">If SoM +5pts</p>
                          <p className="text-sm font-bold text-[var(--success)] font-mono">INR {forecast.som_boost_estimate?.toLocaleString()}</p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="text-[9px] text-[var(--text-tertiary)] text-center py-6 italic">Connect platforms and log revenue to unlock your 12-week forecast.</p>
                  )}
                </div>
              </div>
            )}
          </TabsContent>
          <TabsContent value="citations">
            {citations && (
              <div className="nx-metric-card rounded-md">
                <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">Content Citation Intelligence</h3>
                <p className="text-xs text-[var(--text-tertiary)] mb-3">{citations.cited_count}/{citations.total} posts cited by AI</p>
                <div className="space-y-2">
                  {citations.posts?.map(p => (
                    <div key={p.id} className={`p-3 rounded border ${p.cited ? 'border-[var(--success-border)] bg-[var(--success-bg)]' : 'border-[var(--border)]'}`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-xs text-[var(--text-primary)] font-medium">{p.title}</span>
                        {p.ai_crawled && <span className="encrypted-badge">AI Crawled</span>}
                        {p.cited && <CheckCircle size={12} className="text-[var(--success)]" weight="fill" />}
                      </div>
                      <div className="flex gap-1.5 mb-1.5">
                        {Object.entries(p.checks || {}).map(([k, v]) => (
                          <span key={k} className={`px-1.5 py-0.5 text-[8px] rounded font-mono ${v ? 'bg-[var(--success-bg)] text-[var(--success)]' : 'bg-[var(--error-bg)] text-[var(--error)]'}`}>
                            {k.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                      {!p.cited && p.suggested_rewrite && (
                        <div className="mt-2 p-2 rounded bg-[var(--accent-light)] border border-[var(--accent-border)]">
                          <p className="text-[9px] text-[var(--text-secondary)] mb-1">Suggested rewrite:</p>
                          <p className="text-[10px] text-[var(--text-primary)] italic">{p.suggested_rewrite}</p>
                          <Button size="sm" className="mt-1.5 bg-[var(--accent)] text-white text-[9px] h-6 px-2">Apply Rewrite</Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Vernacular */}
          <TabsContent value="vernacular">
            {vern && (
              <div className="space-y-4">
                <div className="nx-metric-card rounded-md">
                  <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3"><Translate size={16} className="inline mr-2 text-[var(--accent)]" weight="duotone" />Language Performance</h3>
                  <p className="text-[9px] text-[var(--text-tertiary)] font-mono mb-3">Sorted by SoM score — strongest language first</p>
                  <div className="space-y-2">
                    {Object.entries(vern.languages || {}).sort((a, b) => b[1].personal_som - a[1].personal_som).map(([lang, d], i) => (
                      <div key={lang} className={`flex items-center gap-3 p-2 rounded border ${i === 0 ? 'border-[var(--accent-border)] bg-[var(--accent-light)]' : 'border-[var(--border)]'}`}>
                        <span className="text-xs text-[var(--text-primary)] w-16 font-medium">{lang}</span>
                        {i === 0 && <span className="nx-tag nx-tag-blue text-[7px]">Strongest</span>}
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2"><span className="text-[8px] text-[var(--text-tertiary)] w-12">SoM</span><div className="flex-1 geo-bar"><div className="geo-bar-fill" style={{ width: `${d.personal_som}%`, background: 'var(--accent)' }} /></div><span className="text-[9px] font-mono text-[var(--text-primary)] w-10 text-right">{d.personal_som}%</span></div>
                          <div className="flex items-center gap-2"><span className="text-[8px] text-[var(--text-tertiary)] w-12">Conv</span><div className="flex-1 geo-bar"><div className="geo-bar-fill" style={{ width: `${Math.min(d.affiliate_conv_rate * 12, 100)}%`, background: 'var(--success)' }} /></div><span className="text-[9px] font-mono text-[var(--text-primary)] w-10 text-right">{d.affiliate_conv_rate}%</span></div>
                        </div>
                        <button onClick={() => toast.success(`${lang} set as primary GEO language`)}
                          className="px-2 py-1 text-[8px] font-mono rounded border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--accent)] transition-colors duration-200 flex-shrink-0">
                          Set Primary
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                {vern.recommendation && (
                  <div className="hitl-card border-[var(--accent-border)]">
                    <p className="text-[9px] font-mono text-[var(--info)] uppercase tracking-wider mb-2">Language Mix Recommendation</p>
                    <p className="text-xs text-[var(--text-secondary)] leading-relaxed">{vern.recommendation}</p>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Marketplace Card */}
          <TabsContent value="card">
            {card && (
              <div className="nx-metric-card rounded-md max-w-lg">
                <div className="flex items-center gap-2 mb-4"><span className="encrypted-badge">Verified Profile</span></div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between"><span className="text-xs text-[var(--text-tertiary)]">Niche</span><span className="text-xs text-[var(--text-primary)]">{card.niche}</span></div>
                  <div className="flex items-center justify-between"><span className="text-xs text-[var(--text-tertiary)]">Market</span><span className="text-xs text-[var(--text-primary)]">{card.market}</span></div>
                  <div className="flex items-center justify-between"><span className="text-xs text-[var(--text-tertiary)]">SoM Score</span><span className="text-sm font-bold text-[var(--accent)] font-mono">{card.personal_som}%</span></div>
                  <div className="flex items-center justify-between"><span className="text-xs text-[var(--text-tertiary)]">Languages</span><span className="flex gap-1">{card.content_languages?.map(l => <span key={l} className="nx-tag nx-tag-blue text-[8px]">{l}</span>)}</span></div>
                  <div className="flex items-center justify-between"><span className="text-xs text-[var(--text-tertiary)]">Conv Rate</span><span className="text-sm font-bold text-[var(--success)] font-mono">{card.affiliate_conv_rate}%</span></div>
                  <div className="flex items-center justify-between"><span className="text-xs text-[var(--text-tertiary)]">Engagement</span><span className="text-sm font-bold text-[var(--text-primary)] font-mono">{card.engagement_score}/100</span></div>
                  <div className="flex items-center justify-between"><span className="text-xs text-[var(--text-tertiary)]">Pricing</span><span className="text-xs text-[var(--text-primary)] capitalize">{card.pricing_model}</span></div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Pitch Deck */}
          <TabsContent value="pitch">
            <div className="nx-metric-card rounded-md text-center py-8">
              <FileText size={40} className="mx-auto mb-3 text-[var(--text-tertiary)]" />
              <h3 className="font-display text-lg font-bold text-[var(--text-primary)] mb-2">AI Partnership Pitch Deck</h3>
              <p className="text-xs text-[var(--text-secondary)] mb-4 max-w-md mx-auto">Generate a 3-page PDF with verified SoM data, affiliate performance, and auto-generated case study. Includes Martian watermark + QR code linking to your Marketplace Card.</p>
              <div className="flex items-center justify-center gap-4 mb-4 text-[9px] text-[var(--text-tertiary)] font-mono">
                <span>Page 1: Identity Card + QR Code</span>
                <span>Page 2: Performance Data</span>
                <span>Page 3: Case Study</span>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-[var(--neutral-bg)] mb-4">
                <span className="encrypted-badge">PDF Watermark</span>
                <span className="text-[8px] text-[var(--text-tertiary)] font-mono">Martian logo in footer • QR code bottom-right page 1</span>
              </div>
              <br />
              <Button onClick={handlePitch} disabled={genPitch} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
                {genPitch ? <CircleNotch size={16} className="animate-spin mr-2" /> : <FileText size={16} className="mr-2" />}
                Generate My Pitch Deck
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
